import React, { useMemo } from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { contactName } from "utils/fullName";
import { formatCashFull, getCurrencySymbol } from "utils/formatCash";
import {
  InvoiceDetail,
  PaymentTermsLabels,
} from "utils/invoices/invoiceFormat";
import type { InvoicePdfPalette } from "utils/invoices/invoicePdfPalette";

const createStyles = (palette: InvoicePdfPalette) =>
  StyleSheet.create({
    page: { padding: 24, fontSize: 11, fontFamily: "Helvetica" },
    section: { marginBottom: 12 },
    headerRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: 8,
    },
    companyInfo: { flex: 1 },
    logo: { width: 70, height: 70, objectFit: "contain", marginLeft: 12 },
    metaRow: {
      flexDirection: "row",
      justifyContent: "space-between",
      marginBottom: 2,
    },
    metaCol: { flex: 1, paddingRight: 8 },
    metaColRight: { flex: 1, paddingLeft: 8 },
    table: { width: "auto", marginTop: 10, marginBottom: 10 },
    tableRow: { flexDirection: "row" },
    tableHeader: { fontWeight: "bold", backgroundColor: palette.bandFill },
    tableCell: {
      padding: 4,
      borderBottom: `1 solid ${palette.hairline}`,
      fontSize: 10,
      flex: 1,
    },
    tableCellQty: { flex: 0.5, textAlign: "center" },
    tableCellPrice: { flex: 1, textAlign: "right" },
    tableCellTotal: { flex: 1, textAlign: "right" },
    summaryRow: {
      flexDirection: "row",
      justifyContent: "flex-end",
      marginTop: 2,
    },
    summaryLabel: { width: 100, fontWeight: "bold", fontSize: 10 },
    summaryValue: { width: 80, textAlign: "right", fontSize: 10 },
    notes: { marginTop: 16, fontSize: 10 },
    notesLabel: { fontWeight: "bold", marginBottom: 2 },
    initialsAvatar: {
      width: 70,
      height: 70,
      borderRadius: 35,
      backgroundColor: palette.bandFill,
      alignItems: "center",
      justifyContent: "center",
      display: "flex",
    },
    initialsText: {
      fontSize: 28,
      fontWeight: "bold",
    },
  });

function getCompanyInitials(name: string) {
  if (!name) return "";
  const names = name.split(" ");
  return names
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

interface InvoicePDFProps {
  invoice: any;
  /** Resolved by the caller: see invoicePdfPalette. */
  palette: InvoicePdfPalette;
}

const InvoicePDF: React.FC<InvoicePDFProps> = ({ invoice, palette }) => {
  const styles = useMemo(() => createStyles(palette), [palette]);
  // The unit beside a flat amount, which was the literal "$" while the figure
  // next to it already went through the invoice currency.
  const currencySymbol = getCurrencySymbol(invoice?.currency || "USD");
  const companyLogo = invoice.companyDetails?.companyLogoUrl;
  const companyName = invoice.companyDetails?.companyName || "";

  //   ANY CHANGES DONE IN HERE SHOULD BE REFLECTED IN InvoicePreview.tsx

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.headerRow}>
          <View style={styles.companyInfo}>
            <Text style={{ fontSize: 16, fontWeight: "bold", marginBottom: 2 }}>
              {invoice.invoiceName}
            </Text>
            <Text>{invoice.companyDetails?.companyName}</Text>
            <Text>{invoice.companyDetails?.companyPhone}</Text>
            <Text>{invoice.companyDetails?.companyWebsite}</Text>
            <Text>{invoice.companyDetails?.companyEmail}</Text>
          </View>
          {companyLogo ? (
            <Image src={companyLogo} style={styles.logo} />
          ) : (
            <View style={styles.initialsAvatar}>
              <Text style={styles.initialsText}>
                {getCompanyInitials(companyName)}
              </Text>
            </View>
          )}
        </View>

        {/* Invoice Meta */}
        <View style={styles.metaRow}>
          <View style={styles.metaCol}>
            <Text>Invoice Number: {invoice.invoiceNumber}</Text>
            {invoice.purchaseOrderId && (
              <Text>Purchase Order Number: {invoice.purchaseOrderId}</Text>
            )}
            <Text>
              Invoice Date: {new Date(invoice.invoiceDate).toDateString()}
            </Text>
            <Text>
              Due Date:{" "}
              {invoice.dueDate
                ? new Date(invoice.dueDate).toDateString()
                : PaymentTermsLabels[
                    invoice.paymentTerms as keyof typeof PaymentTermsLabels
                  ]}
            </Text>
          </View>
          <View style={styles.metaColRight}>
            <Text>Customer: {contactName(invoice.primaryRecipient)}</Text>
            <Text>
              Address: {invoice.primaryRecipient?.address?.address1},{" "}
              {invoice.primaryRecipient?.address?.city},{" "}
              {invoice.primaryRecipient?.address?.state}{" "}
              {invoice.primaryRecipient?.address?.zipcode}
            </Text>
            <Text>Phone: {invoice.primaryRecipient?.phone}</Text>
            <Text>Email: {invoice.primaryRecipient?.email}</Text>
          </View>
        </View>

        {/* Table */}
        <View style={styles.table}>
          <View style={[styles.tableRow, styles.tableHeader]}>
            <Text style={styles.tableCell}>Product or Service</Text>
            <Text style={styles.tableCell}>Description</Text>
            <Text style={[styles.tableCell, styles.tableCellQty]}>Qty</Text>
            <Text style={[styles.tableCell, styles.tableCellPrice]}>
              Unit Price
            </Text>
            <Text style={[styles.tableCell, styles.tableCellTotal]}>Total</Text>
          </View>
          {(invoice.details ?? []).map((detail: InvoiceDetail, idx: number) => (
            <View style={styles.tableRow} key={idx}>
              <Text style={styles.tableCell}>{detail.productOrService}</Text>
              <Text style={styles.tableCell}>{detail.description}</Text>
              <Text style={[styles.tableCell, styles.tableCellQty]}>
                {detail.quantity}
              </Text>
              <Text style={[styles.tableCell, styles.tableCellPrice]}>
                {formatCashFull(detail.amount, invoice.currency)}
              </Text>
              <Text style={[styles.tableCell, styles.tableCellTotal]}>
                {formatCashFull(detail.total, invoice.currency)}
              </Text>
            </View>
          ))}
        </View>

        {/* Summary */}
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Subtotal:</Text>
          <Text style={styles.summaryValue}>
            {formatCashFull(invoice.subtotal, invoice.currency)}
          </Text>
        </View>
        {(invoice.discountAmount > 0 || invoice.discountAmount < 0) && (
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>
              Discount{" "}
              {invoice.discountType === "PERCENTAGE"
                ? `${invoice.discount}%`
                : currencySymbol}
              :
            </Text>
            <Text style={styles.summaryValue}>
              {formatCashFull(invoice.discountAmount, invoice.currency)}
            </Text>
          </View>
        )}

        {(invoice.taxAmount > 0 || invoice.taxAmount < 0) && (
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>
              Tax{" "}
              {invoice.taxType === "PERCENTAGE"
                ? `${invoice.tax}%`
                : currencySymbol}
              :
            </Text>
            <Text style={styles.summaryValue}>
              {formatCashFull(invoice.taxAmount, invoice.currency)}
            </Text>
          </View>
        )}

        {(invoice.serviceFeeAmount > 0 || invoice.serviceFeeAmount < 0) && (
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>
              Service Fee{" "}
              {invoice.serviceFeeType === "PERCENTAGE"
                ? `${invoice.serviceFee}%`
                : currencySymbol}
              :
            </Text>
            <Text style={styles.summaryValue}>
              {formatCashFull(invoice.serviceFeeAmount, invoice.currency)}
            </Text>
          </View>
        )}

        <View style={styles.summaryRow}>
          <Text style={[styles.summaryLabel, { fontWeight: "bold" }]}>
            Total:
          </Text>
          <Text style={[styles.summaryValue, { fontWeight: "bold" }]}>
            {formatCashFull(invoice.total, invoice.currency)}
          </Text>
        </View>

        {/* Notes */}
        {invoice.note && (
          <View style={styles.notes}>
            <Text style={styles.notesLabel}>Notes:</Text>
            <Text>{invoice.note}</Text>
          </View>
        )}
      </Page>
    </Document>
  );
};

export default InvoicePDF;
